"use client";

import * as React from "react";
import * as ContextMenuPrimitive from "@radix-ui/react-context-menu";
import { CheckIcon, ChevronRightIcon, CircleIcon } from '@/shared/ui/icons';

import { splitNativeProps, type NativeMetadataProps } from "./apple-native";
import { cn } from "./utils";

// ── Generated prop filter ──────────────────────────────────────────────────────
// Strips Generated-injected debug props to prevent React warnings
const filterGeneratedProps = (props: any) => {
  const clean: any = {};
  if (!props) return clean;
  for (const key in props) {
    const normalizedKey = String(key).toLowerCase();
    if (
      !normalizedKey.startsWith("_fg") &&
      !normalizedKey.startsWith("figma") &&
      !normalizedKey.startsWith("node-") &&
      normalizedKey !== "figma_selection_id"
    ) {
      clean[key] = props[key];
    }
  }
  return clean;
};

const RadixContextMenu = ContextMenuPrimitive;
const hasRadixContextMenu =
  typeof RadixContextMenu?.Root !== "undefined" &&
  typeof RadixContextMenu?.Trigger !== "undefined" &&
  typeof RadixContextMenu?.Content !== "undefined";

let hasWarnedMissingRadixContextMenu = false;
const warnMissingRadixContextMenu = () => {
  if (hasWarnedMissingRadixContextMenu) return;
  hasWarnedMissingRadixContextMenu = true;
  console.warn(
    "[ui/context-menu] Radix ContextMenu primitives unavailable. Falling back to basic markup.",
  );
};

const sanitizeTriggerChild = (node: React.ReactNode): React.ReactNode => {
  if (!React.isValidElement(node)) {
    return node;
  }

  // Clone the element with filtered props instead of recreating it
  // This preserves the element type while removing Generated debug props
  const cleanProps = filterGeneratedProps((node.props ?? {}) as Record<string, unknown>);

  return React.cloneElement(node, cleanProps as any);
};

function ContextMenu({
  ...props
}: React.ComponentProps<typeof RadixContextMenu.Root>) {
  if (!hasRadixContextMenu) {
    warnMissingRadixContextMenu();
    const { open, defaultOpen, onOpenChange, modal, dir, ...rest } = props as any;
    return <div data-slot="context-menu-fallback" {...rest} />;
  }
  const cleanProps = filterGeneratedProps(props);
  return <RadixContextMenu.Root data-slot="context-menu" {...cleanProps} />;
}

function ContextMenuTrigger({
  children,
  asChild,
  ...props
}: React.ComponentProps<typeof RadixContextMenu.Trigger>) {
  if (!hasRadixContextMenu) {
    warnMissingRadixContextMenu();
    const { className, disabled, ...rest } = props as any;
    const cleanRest = filterGeneratedProps(rest);
    return (
      <span
        data-slot="context-menu-trigger-fallback"
        className={className}
        aria-disabled={disabled}
        {...cleanRest}
      >
        {children}
      </span>
    );
  }
  const cleanProps = filterGeneratedProps(props);
  
  // Sanitize children when asChild is true to remove Generated props
  const sanitizedChildren = asChild ? sanitizeTriggerChild(children) : children;
  
  return (
    <RadixContextMenu.Trigger
      data-slot="context-menu-trigger"
      asChild={asChild}
      {...cleanProps}
    >
      {sanitizedChildren}
    </RadixContextMenu.Trigger>
  );
}

function ContextMenuGroup({
  ...props
}: React.ComponentProps<typeof RadixContextMenu.Group>) {
  if (!hasRadixContextMenu) {
    warnMissingRadixContextMenu();
    return <div data-slot="context-menu-group-fallback" {...(props as any)} />;
  }
  const cleanProps = filterGeneratedProps(props);
  return (
    <RadixContextMenu.Group data-slot="context-menu-group" {...cleanProps} />
  );
}

function ContextMenuPortal({
  ...props
}: React.ComponentProps<typeof RadixContextMenu.Portal>) {
  if (!hasRadixContextMenu) {
    warnMissingRadixContextMenu();
    return <div data-slot="context-menu-portal-fallback" {...(props as any)} />;
  }
  const cleanProps = filterGeneratedProps(props);
  return (
    <RadixContextMenu.Portal data-slot="context-menu-portal" {...cleanProps} />
  );
}

function ContextMenuSub({
  ...props
}: React.ComponentProps<typeof RadixContextMenu.Sub>) {
  if (!hasRadixContextMenu) {
    warnMissingRadixContextMenu();
    const { open, defaultOpen, onOpenChange, ...rest } = props as any;
    return <div data-slot="context-menu-sub-fallback" {...rest} />;
  }
  const cleanProps = filterGeneratedProps(props);
  return <RadixContextMenu.Sub data-slot="context-menu-sub" {...cleanProps} />;
}

function ContextMenuRadioGroup({
  ...props
}: React.ComponentProps<typeof RadixContextMenu.RadioGroup>) {
  if (!hasRadixContextMenu) {
    warnMissingRadixContextMenu();
    const { value, defaultValue, onValueChange, ...rest } = props as any;
    return <div data-slot="context-menu-radio-group-fallback" {...rest} />;
  }
  const cleanProps = filterGeneratedProps(props);
  return (
    <RadixContextMenu.RadioGroup
      data-slot="context-menu-radio-group"
      {...cleanProps}
    />
  );
}

function ContextMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}: React.ComponentProps<typeof RadixContextMenu.SubTrigger> & {
  inset?: boolean;
}) {
  if (!hasRadixContextMenu) {
    warnMissingRadixContextMenu();
    const { disabled, ...rest } = props as any;
    return (
      <div
        data-slot="context-menu-sub-trigger-fallback"
        data-inset={inset}
        className={cn(
          "flex cursor-default items-center outline-hidden select-none [&_svg]:pointer-events-none [&_svg]:shrink-0",
          "rounded-[var(--ui-menu-item-radius)]",
          "px-3 py-2",
          "text-[length:var(--text-menu)] font-[var(--font-weight-medium)]",
          "text-foreground",
          "hover:bg-muted",
          "focus:bg-muted",
          "data-[state=open]:bg-muted",
          "data-[inset]:pl-8",
          "[&_svg:not([class*='size-'])]:size-4",
          className,
        )}
        aria-disabled={disabled}
        {...rest}
      >
        {children}
        <ChevronRightIcon className="ml-auto" />
      </div>
    );
  }
  const cleanProps = filterGeneratedProps(props);
  return (
    <RadixContextMenu.SubTrigger
      data-slot="context-menu-sub-trigger"
      data-inset={inset}
      className={cn(
        "flex cursor-default items-center outline-hidden select-none [&_svg]:pointer-events-none [&_svg]:shrink-0",
        "rounded-[var(--ui-menu-item-radius)]",
        "px-3 py-2",
        "text-[length:var(--text-menu)] font-[var(--font-weight-medium)]",
        "text-foreground",
        "hover:bg-muted",
        "focus:bg-muted",
        "data-[state=open]:bg-muted",
        "data-[inset]:pl-8",
        "[&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...cleanProps}
    >
      {children}
      <ChevronRightIcon className="ml-auto" />
    </RadixContextMenu.SubTrigger>
  );
}

function ContextMenuSubContent({
  className,
  ...props
}: React.ComponentProps<typeof RadixContextMenu.SubContent>) {
  if (!hasRadixContextMenu) {
    warnMissingRadixContextMenu();
    return (
      <div
        data-slot="context-menu-sub-content-fallback"
        className={cn(
          "z-50 min-w-[12rem] overflow-hidden",
          "rounded-[var(--ui-menu-radius)]",
          "bg-popover text-popover-foreground",
          "border border-border",
          "p-1",
          "shadow-[var(--ui-menu-shadow)]",
          "backdrop-blur-xl",
          className,
        )}
        {...(props as any)}
      />
    );
  }
  const cleanProps = filterGeneratedProps(props);
  return (
    <RadixContextMenu.SubContent
      data-slot="context-menu-sub-content"
      className={cn(
        "z-50 min-w-[12rem] overflow-hidden origin-(--radix-context-menu-content-transform-origin)",
        "rounded-[var(--ui-menu-radius)]",
        "bg-popover text-popover-foreground",
        "border border-border",
        "p-1",
        "shadow-[var(--ui-menu-shadow)]",
        "backdrop-blur-xl",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        "data-[side=bottom]:slide-in-from-top-2",
        "data-[side=left]:slide-in-from-right-2",
        "data-[side=right]:slide-in-from-left-2",
        "data-[side=top]:slide-in-from-bottom-2",
        className,
      )}
      {...cleanProps}
    />
  );
}

function ContextMenuContent({
  className,
  ...props
}: React.ComponentProps<typeof RadixContextMenu.Content> & NativeMetadataProps) {
  const cleanProps = filterGeneratedProps(props);
  const { nativeProps, rest } = splitNativeProps(cleanProps);
  if (!hasRadixContextMenu) {
    warnMissingRadixContextMenu();
    return (
      <div
        data-slot="context-menu-content-fallback"
        data-native-role={nativeProps["data-native-role"] ?? "context-menu"}
        className={cn(
          "z-50 max-h-96 min-w-[12rem] overflow-x-hidden overflow-y-auto",
          "rounded-[var(--ui-menu-radius)]",
          "bg-popover text-popover-foreground",
          "border border-border",
          "p-1",
          "shadow-[var(--ui-menu-shadow)]",
          "backdrop-blur-xl",
          className,
        )}
        {...nativeProps}
        {...(rest as any)}
      />
    );
  }
  return (
    <RadixContextMenu.Portal>
      <RadixContextMenu.Content
        data-slot="context-menu-content"
        data-native-role={nativeProps["data-native-role"] ?? "context-menu"}
        className={cn(
          "z-50 max-h-(--radix-context-menu-content-available-height) min-w-[12rem] overflow-x-hidden overflow-y-auto origin-(--radix-context-menu-content-transform-origin)",
          "rounded-[var(--ui-menu-radius)]",
          "bg-popover text-popover-foreground",
          "border border-border",
          "p-1",
          "shadow-[var(--ui-menu-shadow)]",
          "backdrop-blur-xl",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          "data-[side=bottom]:slide-in-from-top-2",
          "data-[side=left]:slide-in-from-right-2",
          "data-[side=right]:slide-in-from-left-2",
          "data-[side=top]:slide-in-from-bottom-2",
          className,
        )}
        {...nativeProps}
        {...rest}
      />
    </RadixContextMenu.Portal>
  );
}

function ContextMenuItem({
  className,
  inset,
  variant = "default",
  onClick,
  ...props
}: React.ComponentProps<typeof RadixContextMenu.Item> & {
  inset?: boolean;
  variant?: "default" | "destructive";
  onClick?: (event: Event) => void;
} & NativeMetadataProps) {
  const cleanProps = filterGeneratedProps(props);
  const { nativeProps, rest } = splitNativeProps(cleanProps);
  
  // Convert onClick to onSelect for Radix UI compatibility
  const handleSelect = (event: Event) => {
    if (onClick) {
      onClick(event);
    } else if ((rest as any).onSelect) {
      (rest as any).onSelect(event);
    }
  };
  
  if (!hasRadixContextMenu) {
    warnMissingRadixContextMenu();
    const { disabled, onSelect, ...rest } = cleanProps as any;
    return (
      <div
        data-slot="context-menu-item-fallback"
        data-native-role={nativeProps["data-native-role"] ?? "menu-item"}
        data-inset={inset}
        data-variant={variant}
        className={cn(
          "relative flex cursor-default items-center outline-hidden select-none",
          "rounded-[var(--ui-menu-item-radius)]",
          "px-3 py-2 gap-2.5",
          "text-[length:var(--text-menu)] font-[var(--font-weight-medium)]",
          "text-foreground",
          "hover:bg-muted focus:bg-muted",
          "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
          "data-[inset]:pl-8",
          "data-[variant=destructive]:text-destructive",
          "data-[variant=destructive]:hover:bg-destructive/10",
          "dark:data-[variant=destructive]:hover:bg-destructive/20",
          "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
          "[&_svg:not([class*='text-'])]:text-muted-foreground",
          "data-[variant=destructive]:*:[svg]:!text-destructive",
          className,
        )}
        aria-disabled={disabled}
        onClick={handleSelect as any}
        {...nativeProps}
        {...filterGeneratedProps(rest)}
      />
    );
  }
  return (
    <RadixContextMenu.Item
      data-slot="context-menu-item"
      data-native-role={nativeProps["data-native-role"] ?? "menu-item"}
      data-inset={inset}
      data-variant={variant}
      className={cn(
        "relative flex cursor-default items-center outline-hidden select-none",
        "rounded-[var(--ui-menu-item-radius)]",
        "px-3 py-2 gap-2.5",
        "text-[length:var(--text-menu)] font-[var(--font-weight-medium)]",
        "text-foreground",
        "hover:bg-muted focus:bg-muted",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        "data-[inset]:pl-8",
        "data-[variant=destructive]:text-destructive",
        "data-[variant=destructive]:hover:bg-destructive/10",
        "dark:data-[variant=destructive]:hover:bg-destructive/20",
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        "[&_svg:not([class*='text-'])]:text-muted-foreground",
        "data-[variant=destructive]:*:[svg]:!text-destructive",
        className,
      )}
      onSelect={handleSelect as any}
      {...nativeProps}
      {...rest}
    />
  );
}

function ContextMenuCheckboxItem({
  className,
  children,
  checked,
  ...props
}: React.ComponentProps<typeof RadixContextMenu.CheckboxItem>) {
  const cleanProps = filterGeneratedProps(props);
  if (!hasRadixContextMenu) {
    warnMissingRadixContextMenu();
    const { onSelect, disabled, ...rest } = cleanProps as any;
    return (
      <div
        data-slot="context-menu-checkbox-item-fallback"
        className={cn(
          "relative flex cursor-default items-center outline-hidden select-none",
          "rounded-[var(--ui-menu-item-radius)]",
          "py-2 pr-3 pl-8 gap-2",
          "text-[length:var(--text-menu)] font-[var(--font-weight-medium)]",
          "text-foreground",
          "hover:bg-muted focus:bg-muted",
          "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
          "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
          className,
        )}
        aria-disabled={disabled}
        {...rest}
      >
        <span className="pointer-events-none absolute left-3 flex size-3.5 items-center justify-center">
          {checked ? <CheckIcon className="size-4" /> : null}
        </span>
        {children}
      </div>
    );
  }
  return (
    <RadixContextMenu.CheckboxItem
      data-slot="context-menu-checkbox-item"
      className={cn(
        "relative flex cursor-default items-center outline-hidden select-none",
        "rounded-[var(--ui-menu-item-radius)]",
        "py-2 pr-3 pl-8 gap-2",
        "text-[length:var(--text-menu)] font-[var(--font-weight-medium)]",
        "text-foreground",
        "hover:bg-muted focus:bg-muted",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      checked={checked}
      {...cleanProps}
    >
      <span className="pointer-events-none absolute left-3 flex size-3.5 items-center justify-center">
        <RadixContextMenu.ItemIndicator>
          <CheckIcon className="size-4" />
        </RadixContextMenu.ItemIndicator>
      </span>
      {children}
    </RadixContextMenu.CheckboxItem>
  );
}

function ContextMenuRadioItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof RadixContextMenu.RadioItem>) {
  const cleanProps = filterGeneratedProps(props);
  if (!hasRadixContextMenu) {
    warnMissingRadixContextMenu();
    const { onSelect, disabled, ...rest } = cleanProps as any;
    return (
      <div
        data-slot="context-menu-radio-item-fallback"
        className={cn(
          "relative flex cursor-default items-center outline-hidden select-none",
          "rounded-[var(--ui-menu-item-radius)]",
          "py-2 pr-3 pl-8 gap-2",
          "text-[length:var(--text-menu)] font-[var(--font-weight-medium)]",
          "text-foreground",
          "hover:bg-muted focus:bg-muted",
          "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
          "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
          className,
        )}
        aria-disabled={disabled}
        {...rest}
      >
        <span className="pointer-events-none absolute left-3 flex size-3.5 items-center justify-center">
          <CircleIcon className="size-2 fill-current" />
        </span>
        {children}
      </div>
    );
  }
  return (
    <RadixContextMenu.RadioItem
      data-slot="context-menu-radio-item"
      className={cn(
        "relative flex cursor-default items-center outline-hidden select-none",
        "rounded-[var(--ui-menu-item-radius)]",
        "py-2 pr-3 pl-8 gap-2",
        "text-[length:var(--text-menu)] font-[var(--font-weight-medium)]",
        "text-foreground",
        "hover:bg-muted focus:bg-muted",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...cleanProps}
    >
      <span className="pointer-events-none absolute left-3 flex size-3.5 items-center justify-center">
        <RadixContextMenu.ItemIndicator>
          <CircleIcon className="size-2 fill-current" />
        </RadixContextMenu.ItemIndicator>
      </span>
      {children}
    </RadixContextMenu.RadioItem>
  );
}

function ContextMenuLabel({
  className,
  inset,
  ...props
}: React.ComponentProps<typeof RadixContextMenu.Label> & {
  inset?: boolean;
}) {
  const cleanProps = filterGeneratedProps(props);
  if (!hasRadixContextMenu) {
    warnMissingRadixContextMenu();
    return (
      <div
        data-slot="context-menu-label-fallback"
        data-inset={inset}
        className={cn(
          "px-3 py-1.5",
          "text-[length:var(--text-search)] font-[var(--font-weight-semibold)]",
          "text-muted-foreground",
          "data-[inset]:pl-8",
          className,
        )}
        {...cleanProps}
      />
    );
  }
  return (
    <RadixContextMenu.Label
      data-slot="context-menu-label"
      data-inset={inset}
      className={cn(
        "px-3 py-1.5",
        "text-[length:var(--text-search)] font-[var(--font-weight-semibold)]",
        "text-muted-foreground",
        "data-[inset]:pl-8",
        className,
      )}
      {...cleanProps}
    />
  );
}

function ContextMenuSeparator({
  className,
  ...props
}: React.ComponentProps<typeof RadixContextMenu.Separator>) {
  const cleanProps = filterGeneratedProps(props);
  if (!hasRadixContextMenu) {
    warnMissingRadixContextMenu();
    return (
      <div
        data-slot="context-menu-separator-fallback"
        className={cn("bg-border -mx-1 my-1 h-px", className)}
        {...cleanProps}
      />
    );
  }
  return (
    <RadixContextMenu.Separator
      data-slot="context-menu-separator"
      className={cn("bg-border -mx-1 my-1 h-px", className)}
      {...cleanProps}
    />
  );
}

function ContextMenuShortcut({
  className,
  ...props
}: React.ComponentProps<"span">) {
  const cleanProps = filterGeneratedProps(props);
  return (
    <span
      data-slot="context-menu-shortcut"
      className={cn(
        "ml-auto",
        "text-[length:var(--text-search)] font-[var(--font-weight-normal)]",
        "text-muted-foreground",
        "tracking-tight",
        className,
      )}
      {...cleanProps}
    />
  );
}

export {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuCheckboxItem,
  ContextMenuRadioItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuGroup,
  ContextMenuPortal,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuRadioGroup,
};
